import { z } from "zod";
import { paginationResponseType } from "@/types";

// ============ TIPOS COMUNS ============
export type CommentStatus = 'pending' | 'approved' | 'rejected' | 'flagged';
export type CommentStatusResponse = 'PENDING' | 'APPROVED' | 'REJECTED' | 'FLAGGED';
export type CommentType = 'question' | 'feedback';

// ============ CREATE COMMENT ============
export type CommentState = {
    success?: boolean;
    message?: string;
    errors?: {
        name?: string[];
        email?: string[];
        receiveNotifications?: string[];
        content?: string[];
        comment_type?: string[];
    };
    values?: {
        name: string;
        email: string;
        receiveNotifications: boolean;
        content: string;
        comment_type: string;
    };
};

export const commentSchema = z.object({
    name: z.string()
        .min(2, { message: "Nome deve ter pelo menos 2 caracteres" })
        .max(100, { message: "Nome não pode exceder 100 caracteres" })
        .optional(),
    email: z.email({ message: "Por favor, insira um e-mail válido" }).optional(),
    receiveNotifications: z.boolean(),
    content: z.string()
        .min(10, { message: "Comentário deve ter pelo menos 10 caracteres" })
        .max(1000, { message: "Comentário não pode exceder 1000 caracteres" }),
    comment_type: z.enum(['question', 'feedback'])
});

export type CommentTypeForm = {
    content: string;
    comment_type: string;
};

// ============ GET FEEDBACKS ============
export type feedbackResponseType = {
    id: string;
    sequence: number;
    content: string;
    status: string;
    createdAt: string;
    observation: string | null;
    user?: {
        name: string;
        email: string;
    };
    visitor?: {
        name: string;
        email: string;
    };
};

export type FeedbacksResponse = {
    data: feedbackResponseType[];
    pagination: paginationResponseType;
};

export type questionResponseType = {
    id: string;
    sequence: number;
    content: string;
    answer: string;
    status: string;
    createdAt: string;
    user?: {
        name: string;
        email: string;
    };
    visitor?: {
        name: string;
        email: string;
    };
};

export type QuestionsResponse = {
    data: questionResponseType[];
    pagination: paginationResponseType;
};

// ============ UPDATE FEEDBACK ============
export type UpdateFeedbackType = {
    id: string;
    content: string;
    observation: string;
    status: string;
};

export type FeedbackIdResponseType = {
    id: string;
    sequence: number;
    content: string;
    observation: string | null;
    status: string;
    createdAt: string;
    user?: {
        name: string;
        email: string;
    };
    visitor?: {
        name: string;
        email: string;
    };
};

export type FeedbackStateInfo = {
    success?: boolean;
    message?: string;
    errors?: {
        id?: string[];
        content?: string[];
        status?: string[];
        observation?: string[];
    };
    values?: {
        id: string;
        content: string;
        status: string;
        observation?: string[];
    };
};

export const defaultFeedback: FeedbackIdResponseType = {
    id: '',
    sequence: 0,
    content: '',
    status: '',
    createdAt: '',
    observation: null,
    user: {
        name: '',
        email: '',
    },
    visitor: {
        name: '',
        email: '',
    },
};

export const updateFeedbackSchema = z.object({
    id: z.uuid(),
    content: z.string(),
    observation: z.string().optional(),
    status: z.enum(['pending', 'approved', 'rejected', 'flagged'])
});

// ============ UPDATE QUESTION ============
export type UpdateQuestionType = {
    id: string;
    content: string;
    answer: string;
    status: string;
};

export type QuestionIdResponseType = {
    id: string;
    sequence: number;
    content: string;
    answer: string;
    status: string;
    createdAt: string;
    user?: {
        name: string;
        email: string;
    };
    visitor?: {
        name: string;
        email: string;
    };
};

export type QuestionStateInfo = {
    success?: boolean;
    message?: string;
    errors?: {
        id?: string[];
        content?: string[];
        answer?: string[];
        status?: string[];
    };
    values?: {
        id: string;
        content: string;
        answer: string;
        status?: string;
    };
};

export const defaultQuestion: QuestionIdResponseType = {
    id: '',
    sequence: 0,
    content: '',
    answer: '',
    status: '',
    createdAt: "",
    user: {
        name: '',
        email: '',
    },
    visitor: {
        name: '',
        email: '',
    },
};

export const updateQuestionSchema = z.object({
    id: z.uuid(),
    content: z.string(),
    answer: z.string(),
    status: z.enum(['pending', 'approved', 'rejected', 'flagged'])
});

// ============ TIPOS COMPLETOS ============
export type FeedbackResponse = {
    id: string;
    sequence: number;
    content: string;
    observation: string | null;
    status: CommentStatusResponse;
    createdAt: string;
    updatedAt: string | null;
    userId: string | null;
    visitorId: string | null;
    user?: {
        name: string;
        email: string;
    };
    visitor?: {
        name: string;
        email: string;
    };
};

export type QuestionResponse = {
    id: string;
    sequence: number;
    content: string;
    answer: string | null;
    status: CommentStatusResponse;
    createdAt: string;
    updatedAt: string | null;
    userId: string | null;
    visitorId: string | null;
    user?: {
        name: string;
        email: string;
    };
    visitor?: {
        name: string;
        email: string;
    };
};

export type PublicFeedback = {
    sequence: number;
    content: string;
    status: CommentStatusResponse;
    user?: {
        name: string;
    };
    visitor?: {
        name: string;
    };
};

export type PublicFeedbacksResponse = {
    data: PublicFeedback[];
};

// ============ TIPOS PARA FORMULÁRIOS ============
export type CreateCommentFormData = {
    content: string;
    comment_type: CommentType;
    name?: string;
    email?: string;
    receiveNotifications?: boolean;
};

export type UpdateFeedbackFormData = {
    id: string;
    content?: string;
    observation?: string;
    status?: CommentStatus;
};

export type UpdateQuestionFormData = {
    id: string;
    content?: string;
    answer?: string;
    status?: CommentStatus;
};